/**
 *
 * AbsenceDetail
 *
 */

import React, { memo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import {
  flushStateAbsenceDetail,
  loadAbsence,
  changeAbsence,
  deleteAbsence,
} from './actions';

import {
  selectAbsenceData,
  selectAbsenceLoading,
  selectAbsenceError,
  selectAbsenceDeleteSuccessful,
} from './selectors';

// antd component
import { Layout,  Form, Breadcrumb } from 'antd';
const { Content } = Layout;

//components
import Preloader from 'components/Preloader';
import CustomPageHeader from 'components/CustomPageHeader';
import CreateAbsence from 'components/Absences/CreateAbsence';

// init forms
import { initFormsClasses } from 'utils/forms/forms.init';
// helper for forms
import { setFormValues } from 'utils/forms/forms.utils';

// utils
import {
  getAbsenceMainDate,
  getAbsenceStartTime,
} from 'utils/helpers/helper.generateData';


export function AbsenceDetail({
  // main data
  absenceData,
  // states
  deleteSuccessful,
  error,
  loading,
  history,
  // actions
  loadAbsence,
  deleteAbsence,
  changeAbsence,
  flushState,
  //default
  className,
}) {
  useInjectReducer({ key: 'absenceDetail', reducer });
  useInjectSaga({ key: 'absenceDetail', saga });

  const newKey = 'nouvel';
  const timeFormat = 'HH:mm';
  const dateFormat = 'DD.MM.YYYY';

  const { id: initId } = useParams();
  
  const [breadcrumb, setBreadcrumb] = useState([]);

  const generalForm = initFormsClasses();
  // Form initialization for each tab
  const [customForm] = Form.useForm();

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // reset data in the general form
    customForm.resetFields();

    if (!initId || initId == newKey) {
      // Set Breadcrumbs for new absence
      setBreadcrumb([
        {
          path: '/absences',
          exact: true,
          breadcrumbName: 'Absences',
        },
        {
          path: `/absences/${newKey}`,
          breadcrumbName: `Nouvelle Absence`,
        },
      ]);
    } else if (initId && initId !== newKey) {
      loadAbsence(initId);
    }
  }, [initId]);

  useEffect(() => {
    if (absenceData) {
      const mainDate = getAbsenceMainDate(absenceData);
      const time = getAbsenceStartTime(absenceData);
      const absenceDateString =
        initId !== newKey
          ? ` • ${moment(mainDate).format('dd')}, ${moment(mainDate).format(
              dateFormat,
            )} ${time}`
          : '';

      // Set Breadcrumbs
      setBreadcrumb([
        {
          path: '/absences',
          exact: true,
          breadcrumbName: 'Absences',
        },
        {
          path: `/absences/${absenceData?._id}`,
          breadcrumbName: `Absence${absenceDateString}`,
        },
      ]);

      // reset data in the general form
      setFormValues(customForm, generalForm, absenceData);
    }
  }, [absenceData, initId]);

  // Clearing the state after unmounting a component
  // Anything in here is fired on component unmount.
  useEffect(() => {
    return () => {
      flushState();
      setBreadcrumb([]);
    };
  }, []);

  const itemBreadcrumbRender = (route, params, routes, paths) => {
    const last = routes.indexOf(route) === routes.length - 1;
    return last ? (
      <span>{route.breadcrumbName}</span>
    ) : (
      <Link to={route.path}>{route.breadcrumbName}</Link>
    );
  };

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Absence Detail</title>
        <meta name="description" content="Description of Absence Detail" />
      </Helmet>
      <Content className="main-content-container absence-detail-wrapper">
        {!initId && loading && <Preloader />}
        {!loading && initId && (
          <>
            {breadcrumb && (
              <CustomPageHeader
                className="absence-header"
                title={
                  <Breadcrumb
                    className="absence-breadcrumb"
                    routes={breadcrumb}
                    itemRender={itemBreadcrumbRender}
                  />
                }
                subTitlePosition="top"
              />
            )}

            <div className="absence-detail-wrapper__content">
              <CreateAbsence
                isNew={initId == newKey}
                absence={absenceData}
                change={changeAbsence}
              />
            </div>
          </>
        )}
      </Content>
    </Layout>
  );
}

AbsenceDetail.defaultProps = {
  loading: false
};
AbsenceDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

const mapStateToProps = createStructuredSelector({
  loading: selectAbsenceLoading(),
  error: selectAbsenceError(),
  deleteSuccessful: selectAbsenceDeleteSuccessful(),
  absenceData: selectAbsenceData(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushStateAbsenceDetail()),
    loadAbsence: id => dispatch(loadAbsence(id)),
    changeAbsence: data => dispatch(changeAbsence(data)),
    deleteAbsence: id => dispatch(deleteAbsence(id)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AbsenceDetail);
